// src/pages/StockInKeychainPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import { apiUrl } from "../config/api";

/** ---------- Types ---------- */
type KeychainOption = {
  id: string;          // ใช้เป็น string แม้ DB เป็น number
  label: string;
  extraCost: number;   // มาจาก extra_cost
  imageUrl?: string;
  category?: string | null;
};

type KeychainOptionApiRow = {
  id: number | string;
  label: string;
  extra_cost?: number | string;
  extraCost?: number | string;
  imageUrl?: string | null;
  image_path?: string | null;
  category?: string | null;
};

type KeychainMovementPayload = {
    keychain_id: number;  // อิงจาก id ของ keychain_options
    qty: number;
    qty_use: number;      // รอบรับเข้า = 0
    unit_cost: number;
    ref_no: string;
  };

export default function StockInKeychainPage(): JSX.Element {
  /** ---------- State: data ---------- */
  const [options, setOptions] = useState<KeychainOption[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [optionsError, setOptionsError] = useState<string | null>(null);

  /** ---------- State: form ---------- */
  const [selectedId, setSelectedId] = useState<string>("");
  const [selectedInput, setSelectedInput] = useState<string>(""); // ช่อง text หลัก
  const [qty, setQty] = useState<number | "">("");
  const [unitCost, setUnitCost] = useState<number | "">("");
  const [refNo, setRefNo] = useState<string>("");
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string>("");

  /** ---------- State: modal & search ---------- */
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [optionsQuery, setOptionsQuery] = useState<string>("");

  /** ---------- Toast ---------- */
  const [toast, setToast] = useState<{
    show: boolean;
    message: string;
    type?: "success" | "warning" | "info" | "danger";
  }>({
    show: false,
    message: "",
    type: "success",
  });

  const showToast = (
    message: string,
    type: "success" | "warning" | "info" | "danger" = "success"
  ): void => {
    setToast({ show: true, message, type });
  };

  useEffect(() => {
    if (!toast.show) return;
    const t = setTimeout(
      () => setToast({ show: false, message: "", type: toast.type }),
      2200
    );
    return () => clearTimeout(t);
  }, [toast.show]);

  /** ---------- Derived ---------- */
  const selectedOption = useMemo(() => {
    return options.find((o) => o.id === selectedId) ?? null;
  }, [options, selectedId]);

  const filteredOptions = useMemo(() => {
    const q = optionsQuery.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => {
      const cat = (o.category ?? "").toLowerCase();
      return (
        o.label.toLowerCase().includes(q) ||
        o.id.toLowerCase().includes(q) ||
        cat.includes(q)
      );
    });
  }, [options, optionsQuery]);

  /** ---------- Load keychain_options ---------- */
  const loadOptions = async (): Promise<void> => {
    try {
      setLoadingOptions(true);
      setOptionsError(null);

      const resp = await fetch(apiUrl("/keychain_options_list"));
      const json = await resp.json().catch(() => ({} as unknown));
      if (!resp.ok) {
        throw new Error(
          (json as { error?: string }).error ?? resp.statusText
        );
      }

      const rawItems = Array.isArray((json as any)?.items)
        ? ((json as any).items as KeychainOptionApiRow[])
        : [];

      const list: KeychainOption[] = rawItems.map((r) => ({
        id: String(r.id),
        label: String(r.label ?? ""),
        extraCost: Number(r.extra_cost ?? r.extraCost ?? 0),
        imageUrl:
          (r.imageUrl as string | undefined | null) ??
          (r.image_path as string | undefined | null) ??
          undefined,
        category: r.category ?? null,
      }));

      // เรียงตาม id
      list.sort((a, b) => Number(a.id) - Number(b.id));
      setOptions(list);

      // ถ้าเคยเลือกอยู่แล้วให้คงไว้ ถ้าไม่มีก็ตั้งว่าง
      setSelectedId((prev) =>
        prev && list.some((o) => o.id === prev) ? prev : ""
      );
      if (!selectedId && list.length > 0) {
        // default ไม่เลือกอะไร ปล่อย user กดเอง
      }

      showToast("อัปเดตรายการพวงกุญแจแล้ว", "success");
    } catch (e) {
      const msg =
        e instanceof Error
          ? e.message
          : "โหลดข้อมูลพวงกุญแจไม่สำเร็จ";
      setOptionsError(msg);
      showToast(msg, "danger");
    } finally {
      setLoadingOptions(false);
    }
  };

  useEffect(() => {
    void loadOptions();
  }, []);

  /** ---------- Helpers ---------- */
  const resetForm = (): void => {
    setSelectedId("");
    setSelectedInput("");
    setQty("");
    setUnitCost("");
    setRefNo("");
    setErrorMsg("");
  };

  const validate = (): string | null => {
    if (!selectedId) return "กรุณาเลือกพวงกุญแจ / เครื่องประดับ";
    if (qty === "" || Number(qty) <= 0)
      return "จำนวนต้องมากกว่า 0";
    if (unitCost === "" || Number(unitCost) < 0)
      return "ต้นทุนต่อหน่วยต้องเป็นเลข 0 ขึ้นไป";
    return null;
  };

  /** ---------- Submit ---------- */
  const submit = async (): Promise<void> => {
    setErrorMsg("");
    const invalid = validate();
    if (invalid) {
      setErrorMsg(invalid);
      showToast(invalid, "warning");
      return;
    }
    if (saving) return;
  
    const kc = selectedOption;
    const label = kc?.label ?? selectedInput ?? selectedId;
  
    // PMN-CRITICAL(/StockInKeychainPage): this creates real keychain/accessory stock lots used later by StockInPage FIFO; stock-in rows must start with qty_use=0.
    // 👇 เปลี่ยนมาใช้ตาราง keychain_movements
    const payload: KeychainMovementPayload = {
      keychain_id: Number(selectedId),   // id จาก keychain_options
      qty: Number(qty),
      qty_use: 0,                        // รับเข้า ยังไม่ถูกใช้
      unit_cost: Number(unitCost),
      ref_no: refNo.trim() || "-",
    };
  
    try {
      setSaving(true);
      const resp = await fetch(apiUrl("/keychain_movements"), {  // 👈 เปลี่ยน endpoint
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await resp.json().catch(() => ({} as unknown));
  
      if (!resp.ok) {
        const msg =
          (data as { error?: string }).error ??
          resp.statusText ??
          "บันทึกสต๊อกไม่สำเร็จ";
        setErrorMsg(msg);
        showToast(msg, "danger");
        return;
      }
  
      showToast(`เพิ่มสต๊อกให้ “${label}” สำเร็จ`, "success");
      resetForm();
    } catch (e) {
      const msg =
        e instanceof Error
          ? e.message
          : "เชื่อมต่อ API ไม่ได้ — ตรวจสอบเซิร์ฟเวอร์/เน็ตเวิร์ค";
      setErrorMsg(msg);
      showToast(msg, "danger");
    } finally {
      setSaving(false);
    }
  };

  const openOptionsModal = (): void => {
    setOptionsQuery("");
    setShowOptionsModal(true);
  };

  const closeOptionsModal = (): void => setShowOptionsModal(false);

  const chooseOption = (opt: KeychainOption): void => {
    setSelectedId(opt.id);
    setSelectedInput(opt.label);
    setShowOptionsModal(false);
    // โฟกัสไป qty
    setTimeout(
      () =>
        (
          document.getElementById("kc-qty") as HTMLInputElement | null
        )?.focus(),
      0
    );
  };

  /** ---------- UI ---------- */
  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
      {/* Toast มุมขวาบน */}
      <div
        className="position-fixed top-0 end-0 p-3"
        style={{ zIndex: 1080 }}
      >
        {toast.show && (
          <div
            className={`alert alert-${toast.type ?? "success"} shadow mb-0 rounded-3 py-2 px-3`}
          >
            {toast.message}
          </div>
        )}
      </div>

      <div className="container py-4 py-md-5">
        <div className="row justify-content-center">
          <div className="col-12 col-md-8 col-lg-6">
            <div className="card shadow border-0 rounded-4">
              <div className="card-body p-4 p-md-5">
                {/* Header + ปุ่มรีเฟรช */}
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h4 className="mb-0 text-primary">
                    เพิ่มสต๊อกพวงกุญแจ / เครื่องประดับ
                  </h4>

                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => void loadOptions()}
                      disabled={loadingOptions}
                      title="ดึงรายการพวงกุญแจจากฐานข้อมูลอีกครั้ง"
                    >
                      {loadingOptions ? "กำลังรีเฟรช…" : "รีเฟรช"}
                    </button>
                  </div>
                </div>

                <a
                  href="/AddKeychainOptionPage"
                  className="btn btn-success btn-sm d-inline-flex align-items-center gap-2 mb-3"
                  data-bs-toggle="tooltip"
                  data-bs-placement="left"
                  title="ไปหน้าเพิ่มตัวเลือกพวงกุญแจ"
                  aria-label="ไปหน้าเพิ่มตัวเลือกพวงกุญแจ"
                >
                  <span className="fw-bold">+</span> เพิ่มตัวเลือกพวงกุญแจ
                </a>

                {loadingOptions && (
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <div
                      className="spinner-border spinner-border-sm"
                      role="status"
                    />
                    <span>กำลังโหลดรายการพวงกุญแจ…</span>
                  </div>
                )}
                {optionsError && (
                  <div
                    className="alert alert-warning rounded-3"
                    role="alert"
                  >
                    โหลดพวงกุญแจไม่สำเร็จ: {optionsError}
                  </div>
                )}

                {/* กล่องรายละเอียดพวงกุญแจ */}
                <div className="border rounded-3 p-3 bg-white mb-3">
                  {/* เลือกพวงกุญแจ */}
                  <div className="mb-3">
                    <div className="d-flex align-items-center justify-content-between">
                      <label
                        htmlFor="kc-input"
                        className="form-label mb-0"
                      >
                        พวงกุญแจ / เครื่องประดับ
                      </label>

                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1"
                        onClick={openOptionsModal}
                        data-bs-toggle="tooltip"
                        data-bs-placement="left"
                        title="เปิดรายการพวงกุญแจทั้งหมด"
                        aria-label="เปิดรายการพวงกุญแจทั้งหมด"
                      >
                        ดูทั้งหมด
                      </button>
                    </div>

                    <input
                      id="kc-input"
                      className="form-control"
                      list="kc-list"
                      placeholder={
                        loadingOptions
                          ? "กำลังโหลดพวงกุญแจ..."
                          : "พิมพ์ชื่อเพื่อค้นหา หรือเลือกจากรายการ"
                      }
                      value={selectedInput}
                      disabled={loadingOptions}
                      onChange={(e) => {
                        const val = e.target.value;
                        setSelectedInput(val);
                        const found = options.find(
                          (o) => o.label === val
                        );
                        setSelectedId(found ? found.id : "");
                      }}
                      onBlur={(e) => setSelectedInput(e.target.value.trim())}
                    />
                    <datalist id="kc-list">
                      {options.map((o) => (
                        <option
                          key={o.id}
                          value={o.label}
                          label={`${o.label}${
                            o.category ? ` • ${o.category}` : ""
                          }`}
                        />
                      ))}
                    </datalist>

                    {selectedId && !selectedOption && !loadingOptions && (
                      <div className="small text-danger mt-1">
                        ไม่พบตัวเลือกพวงกุญแจที่เลือก กรุณาเลือกใหม่
                      </div>
                    )}

                    {selectedOption && (
                      <div className="small text-muted mt-1">
                        <span className="fw-semibold">
                          {selectedOption.label}
                        </span>
                        {selectedOption.category && (
                          <>
                            {" "}
                            •{" "}
                            <span className="badge bg-light text-muted">
                              {selectedOption.category}
                            </span>
                          </>
                        )}{" "}
                        • ต้นทุนพื้นฐาน:{" "}
                        {selectedOption.extraCost.toFixed(2)} บาท
                      </div>
                    )}
                  </div>

                  {/* qty + cost */}
                  <div className="row g-3">
                    <div className="col-12 col-sm-6">
                      <label
                        htmlFor="kc-qty"
                        className="form-label"
                      >
                        จำนวนที่รับเข้า (qty)
                      </label>
                      <input
                        id="kc-qty"
                        type="number"
                        min={1}
                        step={1}
                        className="form-control"
                        value={qty}
                        onChange={(e) =>
                          setQty(
                            e.target.value === ""
                              ? ""
                              : Math.max(
                                  1,
                                  Math.floor(
                                    Number(e.target.value) || 0
                                  )
                                )
                          )
                        }
                      />
                    </div>

                    <div className="col-12 col-sm-6">
                      <label
                        htmlFor="kc-unitCost"
                        className="form-label"
                      >
                        ต้นทุนพวงกุญแจต่อชิ้น
                      </label>
                      <input
                        id="kc-unitCost"
                        type="number"
                        min={0}
                        step="0.01"
                        className="form-control"
                        value={unitCost}
                        onChange={(e) =>
                          setUnitCost(
                            e.target.value === ""
                              ? ""
                              : Math.max(0, Number(e.target.value))
                          )
                        }
                      />
                      <div className="form-text">
                        สามารถอ้างอิงจากค่า extra cost ตั้งต้น{" "}
                        {selectedOption
                          ? `(${selectedOption.extraCost.toFixed(
                              2
                            )} บาท)`
                          : ""}
                      </div>
                    </div>
                  </div>

                  {/* Ref No */}
                  <div className="mt-3">
                    <label
                      htmlFor="kc-refNo"
                      className="form-label"
                    >
                      เลขอ้างอิง (Ref No.)
                    </label>
                    <input
                      id="kc-refNo"
                      type="text"
                      className="form-control"
                      placeholder="เช่น PO-KC-001 / ใบรับของ"
                      value={refNo}
                      onChange={(e) => setRefNo(e.target.value)}
                    />
                    <div className="form-text">
                      เหตุผลจะตั้งเป็น <code>PURCHASE</code> และตารางอ้างอิงเป็น{" "}
                      <code>purchase</code> อัตโนมัติ
                    </div>
                  </div>
                </div>

                {errorMsg && (
                  <div
                    className="alert alert-warning rounded-3"
                    role="alert"
                  >
                    <div className="fw-semibold">ข้อผิดพลาด:</div>
                    <div className="text-break">{errorMsg}</div>
                  </div>
                )}

                <div className="d-grid">
                  <button
                    type="button"
                    className="btn btn-primary btn-lg"
                    disabled={saving}
                    onClick={() => void submit()}
                  >
                    {saving ? "กำลังบันทึก…" : "💾 บันทึกการรับเข้าสต๊อก"}
                  </button>
                </div>

                <div className="d-flex gap-2 justify-content-between mt-3">
                  <button
                    type="button"
                    className="btn btn-outline-secondary flex-fill"
                    onClick={resetForm}
                  >
                    ล้างฟอร์ม
                  </button>
                  <a
                    href="/"
                    className="btn btn-link text-decoration-none flex-fill text-center"
                  >
                    ← กลับหน้าเมนู
                  </a>
                </div>
              </div>
            </div>

            <div className="text-center small text-muted mt-3">
              * หน้านี้ใช้สไตล์การ์ด / โทนสีเดียวกับหน้ารับเข้าสินค้าหลัก
              เพื่อให้ UI สอดคล้องกัน
            </div>
          </div>
        </div>

        {/* -------- Modal: รายการพวงกุญแจทั้งหมด -------- */}
        {showOptionsModal && (
          <div
            className="modal fade show"
            role="dialog"
            style={{
              display: "block",
              backgroundColor: "rgba(0,0,0,0.5)",
            }}
            aria-modal="true"
            aria-labelledby="keychainOptionsModalLabel"
            onClick={(e) => {
              if (e.target === e.currentTarget) closeOptionsModal();
            }}
          >
            <div className="modal-dialog modal-lg modal-dialog-scrollable">
              <div className="modal-content rounded-4 shadow">
                <div className="modal-header">
                  <h5
                    className="modal-title"
                    id="keychainOptionsModalLabel"
                  >
                    รายการพวงกุญแจ / เครื่องประดับทั้งหมด
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={closeOptionsModal}
                  />
                </div>

                <div className="modal-body">
                  <div className="input-group mb-3">
                    <span className="input-group-text">ค้นหา</span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="พิมพ์ชื่อ / id / กลุ่ม"
                      value={optionsQuery}
                      onChange={(e) => setOptionsQuery(e.target.value)}
                      autoFocus
                    />
                  </div>

                  <div
                    className="table-responsive"
                    style={{ maxHeight: 420 }}
                  >
                    <table className="table table-hover align-middle mb-0">
                      <thead className="table-light sticky-top">
                        <tr>
                          <th style={{ width: 72 }}>รูป</th>
                          <th style={{ width: 80 }}>ID</th>
                          <th>ชื่อ</th>
                          <th style={{ width: 120 }}>กลุ่ม</th>
                          <th
                            className="text-end"
                            style={{ width: 140 }}
                          >
                            Extra cost
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredOptions.map((o) => (
                          <tr
                            key={o.id}
                            role="button"
                            className="user-select-none"
                            onClick={() => chooseOption(o)}
                          >
                            <td>
                              {o.imageUrl ? (
                                <img
                                  src={o.imageUrl}
                                  alt={o.label}
                                  width={56}
                                  height={56}
                                  style={{
                                    objectFit: "cover",
                                    borderRadius: 8,
                                    background: "#f5f5f5",
                                  }}
                                  onError={(e) => {
                                    const img = e.currentTarget;
                                    img.onerror = null;
                                    img.src = "/no-image.png";
                                  }}
                                />
                              ) : (
                                <div
                                  className="bg-light border rounded"
                                  style={{
                                    width: 56,
                                    height: 56,
                                  }}
                                />
                              )}
                            </td>
                            <td className="fw-semibold">{o.id}</td>
                            <td>{o.label}</td>
                            <td>
                              {o.category ? (
                                <span className="badge bg-light text-muted">
                                  {o.category}
                                </span>
                              ) : (
                                <span className="text-muted small">
                                  -
                                </span>
                              )}
                            </td>
                            <td className="text-end">
                              +{o.extraCost.toFixed(2)} บาท
                            </td>
                          </tr>
                        ))}
                        {filteredOptions.length === 0 && (
                          <tr>
                            <td
                              colSpan={5}
                              className="text-center text-muted py-3"
                            >
                              ไม่พบข้อมูลตามเงื่อนไขค้นหา
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="modal-footer">
                  <div className="me-auto small text-muted">
                    ทั้งหมด {options.length.toLocaleString()} ตัวเลือก
                  </div>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeOptionsModal}
                  >
                    ปิดหน้าต่าง
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
